<script>
import axios from "axios";
import { mapActions } from "vuex";

export default {
  name: "AdminPromotionForm",

  props: {
    contract: { // you pass :contract="application"
      type: Object,
      required: true,
    },
  },

  data: () => ({
    formData: {
      id: null,
      promotion: null,
      isPercent: false,
    },

    isReadonly: false,

    templatesLoading: false,
    templatesError: null,
    templates: [],
    selectedTemplateId: null,
  }),

  created() {
    // seed from contract.promotion (just a number)
    if (this.contract && this.contract.promotion != null) {
      this.formData.promotion = Number(this.contract.promotion);
      // if you always want percent mode, set true; else default to false
      this.formData.isPercent = this.contract.isPercent;
    }
    this.fetchTemplates();
  },

  computed: {
    templateOptions() {
      return (this.templates || []).map((t) => {
        const label = t.isPercent
            ? `${t.promotion} %`
            : `${this.formatNumber(t.promotion)} UZS`;
        return { id: t.id, label, raw: t };
      });
    },
  },

  methods: {
    ...mapActions("admin-contract2026", ["SAVE_APPLICATION_PROMOTION"]),

    async fetchTemplates() {
      this.templatesLoading = true;
      this.templatesError = null;
      try {
        const { data } = await axios.get("/admin/promotions");
        this.templates = Array.isArray(data) ? data : [];
        this.syncSelectedFromForm();
      } catch (e) {
        this.templatesError = this.$t("Failed to load templates");
      } finally {
        this.templatesLoading = false;
      }
    },

    // select the template that matches contract.promotion
    syncSelectedFromForm() {
      if (!this.templates?.length) return;
      if (this.formData?.promotion == null) {
        this.selectedTemplateId = null;
        return;
      }
      const currentPromotion = Number(this.formData.promotion);
      const match = this.templates.find(
          (t) => Number(t.promotion) === currentPromotion
      );
      this.selectedTemplateId = match ? match.id : null;
    },

    onSelectTemplate(id) {
      this.selectedTemplateId = id;
      const found = this.templateOptions.find((o) => o.id === id);
      if (found && found.raw) {
        this.formData.isPercent = found.raw.isPercent;
        this.formData.promotion = found.raw.promotion;
      }
    },

    formatNumber(n) {
      try {
        return Number(n).toLocaleString("uz-UZ");
      } catch {
        return n;
      }
    },

    onSend() {
      const params = {
        ...this.formData,
        contractId: this.contract?.id,
      };
      this.SAVE_APPLICATION_PROMOTION(params).then((status) => {
        if (status != null) {
          window.location.reload();
        }
      });
    },
  },
};
</script>


<template>
  <v-form
      :readonly="isReadonly"
      @submit.prevent="() => false"
      ref="form"
      lazy-validation
      class="promo-form"
  >
    <div class="section-header">{{ $t("Promition miqdori") }}</div>

    <div class="py-13 px-6">
      <div class="row">
        <div class="cols-12 col-md-5">
          <div>
            <div class="form-label">
              {{ $t("Attention promotion") }} <span>*</span>
            </div>

            <!-- Only template dropdown -->
            <div class="template-row">
              <v-select
                  class="template-select"
                  :items="templateOptions"
                  :loading="templatesLoading"
                  :disabled="templatesLoading || !templateOptions.length"
                  :label="$t('Choose template')"
                  item-text="label"
                  item-value="id"
                  dense
                  outlined
                  clearable
                  v-model="selectedTemplateId"
                  @change="onSelectTemplate"
                  hide-details="auto"
                  attach
                  :menu-props="{ offsetY: true, bottom: true, maxHeight: 300 }"
              />
              <div v-if="templatesError" class="template-error">
                {{ templatesError }}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div class="row">
      <div align="center" class="col-12">
        <v-btn @click="onSend" color="accent" class="save-btn" large>
          {{ $t("Save") }}
        </v-btn>
      </div>
    </div>
  </v-form>
</template>

<style lang="scss" scoped>
.promo-form {
  .section-header {
    background: #0096db;
    border-radius: 8px;
    display: flex;
    justify-content: center;
    align-items: center;
    font-size: 1.15rem;
    color: #fff;
    padding: 8px 10px;
    letter-spacing: 0.2px;
    box-shadow: 0 2px 10px rgba(0,0,0,0.06);
  }

  .form-label {
    font-weight: 600;
    margin: 10px 0 6px;
    color: #2a2e33;

    span {
      padding-left: 2px;
      color: #e53935;
    }
  }

  .template-row {
    margin-bottom: 8px;

    .template-select {
      :deep(.v-input__slot) {
        border-radius: 10px !important;
      }
    }

    .template-error {
      margin-top: 6px;
      font-size: 0.875rem;
      color: #e53935;
    }
  }

  .save-btn {
    border-radius: 10px;
    text-transform: none;
    font-weight: 600;
    padding: 10px 24px;
    box-shadow: 0 6px 18px rgba(0,0,0,0.06);
  }
}
</style>
